import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Search, Modal, InlineNotification, Dropdown, TextArea, Loading, Pagination } from '@carbon/react';
import { Analytics, Edit, View, Temperature, CheckmarkFilled, WarningAltFilled, CloudOffline, Information, Add, List, Grid as GridIcon, Renew } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import SubscriptionGate from '../components/SubscriptionGate';
import { CONTEXT_TYPES } from '../constants/intelligenceContexts';

const IntelligencePage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { canUseAIAnalysis } = useSubscription();
  const { sensors, isLoading: sensorsLoading, refetch } = useSensorsOptimized();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [contextFilter, setContextFilter] = useState('all');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [selectedContext, setSelectedContext] = useState(null);
  const [customNotes, setCustomNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [sensorContexts, setSensorContexts] = useState({});
  const [isLoadingContexts, setIsLoadingContexts] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  useEffect(() => {
    const loadContexts = async () => {
      if (!user) return;
      try {
        setIsLoadingContexts(true);
        const { data, error } = await supabase.from('sensor_contexts').select('*').eq('user_id', user.id);
        if (error) { setSensorContexts({}); }
        else if (data) {
          const contextMap = {};
          data.forEach(ctx => { contextMap[ctx.sensor_id] = ctx; });
          setSensorContexts(contextMap);
        }
      } catch (err) { console.error('Error loading contexts:', err); }
      finally { setIsLoadingContexts(false); }
    };
    loadContexts();
  }, [user]);

  const getStatusIcon = (status) => {
    if (status === 'online') return <CheckmarkFilled size={16} style={{ color: '#24a148' }} />;
    if (status === 'warning') return <WarningAltFilled size={16} style={{ color: '#ff832b' }} />;
    return <CloudOffline size={16} style={{ color: '#da1e28' }} />;
  };

  const getDeviceStatus = (latestReading) => {
    if (!latestReading) return 'offline';
    const timestamp = latestReading?.created_at || latestReading?.recorded_at;
    const diffMins = Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000);
    if (diffMins > 60) return 'offline';
    if (diffMins > 15) return 'warning';
    return 'online';
  };

  const sensorsWithContext = useMemo(() => {
    return sensors.map(sensor => {
      const context = sensorContexts[sensor.id];
      const contextType = context ? CONTEXT_TYPES.find(c => c.id === context.context_type) : null;
      return { ...sensor, status: getDeviceStatus(sensor.latest_reading), currentTemp: sensor.latest_reading?.temperature, currentHumidity: sensor.latest_reading?.humidity, context, contextType, hasContext: !!context };
    });
  }, [sensors, sensorContexts]);

  const filteredSensors = sensorsWithContext.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesContext = contextFilter === 'all' ||
      (contextFilter === 'with_context' && s.hasContext) ||
      (contextFilter === 'no_context' && !s.hasContext) ||
      (s.context?.context_type === contextFilter);
    return matchesSearch && matchesContext;
  });

  const contextStats = useMemo(() => ({ withContext: sensorsWithContext.filter(s => s.hasContext).length, withoutContext: sensorsWithContext.filter(s => !s.hasContext).length, total: sensorsWithContext.length }), [sensorsWithContext]);

  // Pagination
  const totalItems = filteredSensors.length;
  const startIndex = (page - 1) * pageSize;
  const paginatedSensors = viewMode === 'table' ? filteredSensors.slice(startIndex, startIndex + pageSize) : filteredSensors;

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [searchTerm, contextFilter, viewMode]);

  // Filter options
  const filterItems = [
    { id: 'all', text: 'All Sensors' },
    { id: 'with_context', text: 'With Context' },
    { id: 'no_context', text: 'Needs Context' },
    ...CONTEXT_TYPES.map(c => ({ id: c.id, text: `${c.icon} ${c.label}` }))
  ];

  const handleEditContext = (sensor) => {
    setSelectedSensor(sensor);
    const existing = sensorContexts[sensor.id];
    setSelectedContext(existing?.context_type || null);
    setCustomNotes(existing?.custom_notes || '');
    setEditModalOpen(true);
  };

  const handleSaveContext = async () => {
    if (!selectedSensor || !selectedContext) return;
    setIsSaving(true);
    try {
      const contextData = { sensor_id: selectedSensor.id, user_id: user.id, context_type: selectedContext, custom_notes: customNotes, updated_at: new Date().toISOString() };
      const { error } = await supabase.from('sensor_contexts').upsert(contextData, { onConflict: 'sensor_id,user_id' });
      if (error) throw error;
      setSensorContexts(prev => ({ ...prev, [selectedSensor.id]: contextData }));
      setNotification({ kind: 'success', title: 'Context saved', subtitle: `Intelligence context for ${selectedSensor.name} updated.` });
      setEditModalOpen(false);
      setTimeout(() => setNotification(null), 5000);
    } catch (err) {
      setNotification({ kind: 'error', title: 'Save failed', subtitle: err.message });
      setTimeout(() => setNotification(null), 5000);
    } finally { setIsSaving(false); }
  };

  if (sensorsLoading || isLoadingContexts) {
    return <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loading description="Loading..." withOverlay={false} /></div>;
  }

  return (
    <SubscriptionGate 
      feature="AI analysis and intelligence features" 
      disabled={!canUseAIAnalysis}
      customMessage="You need an active subscription to access AI-powered analysis and intelligence features for your cold chain monitoring."
    >
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }}>
      <Modal open={editModalOpen} modalHeading={`Set Context - ${selectedSensor?.name || ''}`} primaryButtonText="Save" secondaryButtonText="Cancel" onRequestClose={() => setEditModalOpen(false)} onRequestSubmit={handleSaveContext} primaryButtonDisabled={isSaving || !selectedContext} size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '16px 0' }}>
          <p style={{ color: textSecondary }}>Assign context to help AI understand what this sensor monitors.</p>
          <Dropdown id="context-type" titleText="Context Type" label="Select type" items={CONTEXT_TYPES} itemToString={(item) => item ? `${item.icon} ${item.label}` : ''} selectedItem={CONTEXT_TYPES.find(c => c.id === selectedContext)} onChange={({ selectedItem }) => setSelectedContext(selectedItem?.id)} />
          {selectedContext && (
            <div style={{ padding: '16px', backgroundColor: isDark ? '#353535' : '#e0e0e0', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '1.5rem' }}>{CONTEXT_TYPES.find(c => c.id === selectedContext)?.icon}</span>
                <h4 style={{ color: textPrimary, margin: 0 }}>{CONTEXT_TYPES.find(c => c.id === selectedContext)?.label}</h4>
                {CONTEXT_TYPES.find(c => c.id === selectedContext)?.critical && <Tag type="red" size="sm">Critical</Tag>}
              </div>
              <p style={{ color: textSecondary, fontSize: '0.875rem' }}>{CONTEXT_TYPES.find(c => c.id === selectedContext)?.description}</p>
            </div>
          )}
          <TextArea id="notes" labelText="Notes (Optional)" placeholder="Add specific details..." value={customNotes} onChange={(e) => setCustomNotes(e.target.value)} rows={3} />
        </div>
      </Modal>

      <Modal open={viewModalOpen} modalHeading={`Context - ${selectedSensor?.name || ''}`} passiveModal onRequestClose={() => setViewModalOpen(false)} size="md">
        {selectedSensor && (
          <div style={{ padding: '16px 0' }}>
            {selectedSensor.contextType ? (
              <div style={{ padding: '16px', backgroundColor: isDark ? '#262626' : '#f4f4f4', borderRadius: '8px', border: `2px solid ${selectedSensor.contextType.critical ? '#da1e28' : '#0f62fe'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '2rem' }}>{selectedSensor.contextType.icon}</span>
                  <h4 style={{ color: textPrimary, margin: 0 }}>{selectedSensor.contextType.label}</h4>
                </div>
                <p style={{ color: textSecondary }}>{selectedSensor.contextType.description}</p>
                {selectedSensor.context?.custom_notes && <p style={{ color: textPrimary, marginTop: '12px', padding: '12px', backgroundColor: isDark ? '#353535' : '#e0e0e0', borderRadius: '4px' }}>{selectedSensor.context.custom_notes}</p>}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <Information size={32} style={{ color: textSecondary }} />
                <p style={{ color: textSecondary }}>No context assigned.</p>
                <Button kind="primary" size="sm" style={{ marginTop: '16px' }} onClick={() => { setViewModalOpen(false); handleEditContext(selectedSensor); }}>Add Context</Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {notification && <div style={{ position: 'fixed', top: '80px', right: '16px', zIndex: 9999 }}><InlineNotification kind={notification.kind} title={notification.title} subtitle={notification.subtitle} onCloseButtonClick={() => setNotification(null)} lowContrast /></div>}

      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '24px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <Analytics size={32} style={{ color: '#8a3ffc' }} />
              <div>
                <h1 style={{ color: textPrimary, fontSize: '1.75rem', fontWeight: 600, margin: 0 }}>Intelligence</h1>
                <p style={{ color: textSecondary, fontSize: '0.875rem', margin: 0 }}>Configure AI context for smarter analysis</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <Tile style={{ padding: '12px 16px', backgroundColor: bgPrimary }}><p style={{ color: textSecondary, fontSize: '0.75rem' }}>Total</p><p style={{ color: textPrimary, fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>{contextStats.total}</p></Tile>
              <Tile style={{ padding: '12px 16px', backgroundColor: bgPrimary }}><p style={{ color: textSecondary, fontSize: '0.75rem' }}>With Context</p><p style={{ color: '#24a148', fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>{contextStats.withContext}</p></Tile>
              <Tile style={{ padding: '12px 16px', backgroundColor: bgPrimary }}><p style={{ color: textSecondary, fontSize: '0.75rem' }}>Needs Context</p><p style={{ color: '#ff832b', fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>{contextStats.withoutContext}</p></Tile>
            </div>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
                <Search labelText="Search" placeholder="Search sensors..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="lg" />
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Button kind={viewMode === 'cards' ? 'secondary' : 'ghost'} size="sm" hasIconOnly renderIcon={GridIcon} iconDescription="Card view" onClick={() => setViewMode('cards')} />
                <Button kind={viewMode === 'table' ? 'secondary' : 'ghost'} size="sm" hasIconOnly renderIcon={List} iconDescription="Table view" onClick={() => setViewMode('table')} />
                <Button kind="ghost" size="sm" hasIconOnly renderIcon={Renew} iconDescription="Refresh" onClick={() => refetch()} />
              </div>
              <div style={{ minWidth: '200px' }}>
                <Dropdown
                  id="context-filter"
                  titleText="Filter by Context"
                  label="Select filter"
                  items={filterItems}
                  itemToString={(item) => item?.text || ''}
                  selectedItem={filterItems.find(f => f.id === contextFilter)}
                  onChange={({ selectedItem }) => setContextFilter(selectedItem?.id || 'all')}
                />
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      <div style={{ padding: '24px 0' }}>
        <Grid>
          {filteredSensors.length === 0 ? (
            <Column lg={16}><Tile style={{ padding: '48px', textAlign: 'center', backgroundColor: bgSecondary }}><Temperature size={48} style={{ color: textSecondary }} /><h3 style={{ color: textPrimary }}>No sensors found</h3><Button kind="primary" renderIcon={Add} onClick={() => navigate('/dashboard/devices/add')} style={{ marginTop: '16px' }}>Add Device</Button></Tile></Column>
          ) : viewMode === 'cards' ? (
            // Card View
            filteredSensors.map((sensor) => (
              <Column lg={8} md={4} sm={4} key={sensor.id}>
                <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, borderLeft: sensor.hasContext ? `4px solid ${sensor.contextType?.critical ? '#da1e28' : '#24a148'}` : `4px solid ${borderColor}`, padding: 0, marginBottom: '16px' }}>
                  <div style={{ padding: '16px', borderBottom: `1px solid ${borderColor}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{getStatusIcon(sensor.status)}<h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0 }}>{sensor.name}</h3></div>
                      {sensor.hasContext && <span style={{ fontSize: '1.5rem' }}>{sensor.contextType?.icon}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}><Tag type="blue" size="sm">{sensor.device_id || 'Sensor'}</Tag>{sensor.hasContext ? <Tag type={sensor.contextType?.critical ? 'red' : 'green'} size="sm">{sensor.contextType?.label}</Tag> : <Tag type="warm-gray" size="sm">No Context</Tag>}</div>
                  </div>
                  <div style={{ padding: '16px' }}>
                    <p style={{ color: textSecondary, fontSize: '0.875rem', margin: '0 0 12px 0' }}>{sensor.hasContext ? sensor.contextType?.description?.substring(0, 80) + '...' : 'Add context to improve AI analysis.'}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div><p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Temp</p><p style={{ color: '#0f62fe', fontSize: '1rem', fontWeight: 600, margin: 0 }}>{sensor.currentTemp?.toFixed(1) || '--.-'}°C</p></div>
                      <div><p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Humidity</p><p style={{ color: '#6929c4', fontSize: '1rem', fontWeight: 600, margin: 0 }}>{sensor.currentHumidity?.toFixed(1) || 'N/A'}%</p></div>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button kind="ghost" size="sm" renderIcon={View} onClick={() => { setSelectedSensor(sensor); setViewModalOpen(true); }}>View</Button>
                    <Button kind="secondary" size="sm" renderIcon={Edit} onClick={() => handleEditContext(sensor)}>{sensor.hasContext ? 'Edit' : 'Add'}</Button>
                  </div>
                </Tile>
              </Column>
            ))
          ) : (
            // Table View
            <Column lg={16} md={8} sm={4}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                  <thead>
                    <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Status</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Sensor</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Context</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Temp</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Humidity</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Expected Range</th>
                      <th style={{ padding: '16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSensors.map((sensor, idx) => (
                      <tr key={sensor.id} style={{ borderBottom: `1px solid ${borderColor}`, backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary }}>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {getStatusIcon(sensor.status)}
                            <span style={{ color: sensor.status === 'online' ? '#24a148' : sensor.status === 'warning' ? '#ff832b' : '#da1e28', textTransform: 'capitalize' }}>{sensor.status}</span>
                          </div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div><p style={{ color: textPrimary, fontWeight: 500, marginBottom: '4px' }}>{sensor.name}</p><Tag type="blue" size="sm">{sensor.device_id || 'Sensor'}</Tag></div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          {sensor.hasContext ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '1.25rem' }}>{sensor.contextType?.icon}</span>
                              <div>
                                <Tag type={sensor.contextType?.critical ? 'red' : 'green'} size="sm">{sensor.contextType?.label}</Tag>
                                {sensor.contextType?.critical && <span style={{ color: '#da1e28', fontSize: '0.75rem', marginLeft: '4px' }}>Critical</span>}
                              </div>
                            </div>
                          ) : (
                            <Tag type="warm-gray" size="sm">No Context</Tag>
                          )}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ color: '#0f62fe', fontWeight: 600 }}>{sensor.currentTemp?.toFixed(1) || '--.-'}°C</span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ color: '#6929c4', fontWeight: 600 }}>{sensor.currentHumidity?.toFixed(1) || 'N/A'}%</span>
                        </td>
                        <td style={{ padding: '16px', color: textSecondary, fontSize: '0.875rem' }}>
                          {sensor.contextType?.tempRange || '-'}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={View} iconDescription="View" onClick={() => { setSelectedSensor(sensor); setViewModalOpen(true); }} />
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={Edit} iconDescription="Edit" onClick={() => handleEditContext(sensor)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalItems > 0 && (
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    backwardText="Previous"
                    forwardText="Next"
                    itemsPerPageText="Items per page:"
                    page={page}
                    pageSize={pageSize}
                    pageSizes={[10, 20, 30, 50]}
                    totalItems={totalItems}
                    onChange={({ page, pageSize }) => { setPage(page); setPageSize(pageSize); }}
                  />
                </div>
              )}
            </Column>
          )}
        </Grid>
      </div>
    </div>
    </SubscriptionGate>
  );
};

export default IntelligencePage;
